import React, { useState, useRef } from "react";
import {
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Stack,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Icon } from "@iconify/react";
import cameraFill from "@iconify/icons-eva/camera-fill";

import Page from "../../components/Page";
import { project_name, s3baseUrl } from "../../config/config";
import { updateAdminProfile } from "../../DAL/AUTH";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { usePGIMode } from "../../Hooks/PGIModeContext";

export default function Profile() {
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setProfileNewImage, setUserName } = usePGIMode();

  const [profile, setProfile] = useState({
    name: localStorage.getItem("name") || "",
    email: localStorage.getItem("email") || "",
    image: localStorage.getItem("image") || "",
    // is_2f_enabled: localStorage.getItem("is_2f_enabled") || false,
  });
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const handleProfileChange = (field) => (event) => {
    setProfile((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      event.target.value = "";
    } else {
      setImageFile(null);
      setImagePreview(null);
      event.target.value = "";
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      // formData.append("is_2f_enabled", profile.is_2f_enabled);
      formData.append("name", profile.name);
      formData.append("email", profile.email);
      if (imageFile) {
        formData.append("image", imageFile);
      }
      console.log(...formData);
      const response = await updateAdminProfile(formData);
      if (response.code === 200) {
        const updatedAdmin = response.admin;
        // localStorage.setItem("is_2f_enabled", updatedAdmin.is_2f_enabled);
        localStorage.setItem("name", updatedAdmin.name);
        setUserName(updatedAdmin.name);

        const newImage = updatedAdmin.image || null;
        if (newImage) {
          localStorage.setItem("image", newImage);
          setProfileNewImage(imagePreview || newImage);
        }
        setProfile((prev) => ({
          ...prev,
          name: updatedAdmin.name,
          image: newImage,
        }));
        setImagePreview(null);
        setImageFile(null);
        // navigate("/dashboard");
        enqueueSnackbar("Profile updated successfully", { variant: "success" });
      } else {
        enqueueSnackbar(response.message, {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const currentImage =
    imagePreview ||
    (profile.image
      ? `${s3baseUrl}${profile.image}?t=${new Date().getTime()}`
      : null);

  return (
    <Page
      title={`Profile - ${localStorage.getItem("metaTitle") || project_name}`}
    >
      <Box sx={{ mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h4" sx={{ mb: 5, color: "primary.main" }}>
          Profile Settings
        </Typography>

        <Card sx={{ maxWidth: 750, boxShadow: 3, mx: "auto" }}>
          <CardContent
            sx={{
              px: { xs: 1.5, md: 3 },
              py: { xs: 2, md: 4 },
            }}
          >
            <form onSubmit={handleProfileSubmit}>
              <Stack spacing={3}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={currentImage}
                      alt={profile.name}
                      sx={{ width: 120, height: 120 }}
                    >
                      {profile.name.charAt(0)}
                    </Avatar>
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        bgcolor: "primary.main",
                        color: "white",
                        "&:hover": { bgcolor: "primary.dark" },
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Icon icon={cameraFill} />
                    </IconButton>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profile.email}
                  onChange={handleProfileChange("email")}
                  disabled
                  helperText="Email cannot be changed"
                />

                <TextField
                  fullWidth
                  label="Full Name"
                  value={profile.name}
                  onChange={handleProfileChange("name")}
                  required
                />

                {/* 2FA Enable/Disable */}
                {/* <FormControl fullWidth>
                  <InputLabel>Two-Factor Authentication</InputLabel>
                  <Select
                    value={profile.is_2f_enabled}
                    label="Two-Factor Authentication"
                    onChange={handleProfileChange("is_2f_enabled")}
                  >
                    <MenuItem value="true">Enabled</MenuItem>
                    <MenuItem value="false">Disabled</MenuItem>
                  </Select>
                </FormControl> */}

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    {loading ? "Updating..." : "Update"}
                  </Button>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Page>
  );
}
